/**
 * Fantasy Console 0 - Editor App
 */

import { useCallback, useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Layout } from './components/Layout/Layout'
import { useCart } from './context/CartContext'
import { useToast } from './context/ToastContext'
import { useAuth } from './context/AuthContext'
import { useAuthManager } from './components/Auth/AuthManager'
import { useConsole } from './context/ConsoleContext'
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts'
import { exportCartAsFile, importCartFromFile } from './utils/storage'
import { countTokensInFiles, MAX_TOKENS } from './utils/tokenCounter'
import { NewCartModal } from './components/Modals/NewCartModal'
import { ShortcutsModal } from './components/Modals/ShortcutsModal'

export default function App() {
  const location = useLocation()
  const {
    cart,
    setActiveTab,
    isDirty,
    loadCart,
    saveToCloud,
    cloudSaveEnabled,
    updateCloudSaveHash,
  } = useCart()
  const { info, success, error, warning } = useToast()
  const { user } = useAuth()
  const { openSignIn } = useAuthManager()
  const { reload, pauseResume } = useConsole()
  const [isSaving, setIsSaving] = useState(false)
  const [isNewCartModalOpen, setIsNewCartModalOpen] = useState(false)
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSave = useCallback(async () => {
    if (!cloudSaveEnabled) {
      info('Cloud storage is not configured.')
      return
    }

    if (!user) {
      openSignIn()
      return
    }

    if (isSaving) return

    const tokenCounts = countTokensInFiles(cart.codeFiles)
    if (tokenCounts.total > MAX_TOKENS) {
      warning(
        `Cart exceeds the ${MAX_TOKENS} token limit. Reduce your code to save to cloud.`,
      )
      return
    }

    const isCartOwner = !cart.userId || cart.userId === user.id
    if (!isCartOwner) {
      info(
        'Fork this cart to save your changes (use the Save button in the header).',
      )
      return
    }

    try {
      setIsSaving(true)
      await saveToCloud()
      updateCloudSaveHash()
      success('Cart saved to cloud')
    } catch (err) {
      error(err instanceof Error ? err.message : 'Failed to save cart to cloud')
    } finally {
      setIsSaving(false)
    }
  }, [
    cloudSaveEnabled,
    user,
    isSaving,
    cart.codeFiles,
    cart.userId,
    saveToCloud,
    updateCloudSaveHash,
    info,
    warning,
    success,
    error,
    openSignIn,
  ])

  const handleNewCart = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to create a new cart?',
      )
      if (!confirmed) return
    }
    setIsNewCartModalOpen(true)
  }, [isDirty])

  const handleImportCart = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to import a new cart?',
      )
      if (!confirmed) return
    }
    fileInputRef.current?.click()
  }, [isDirty])

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      try {
        const importedCart = await importCartFromFile(file)
        loadCart(importedCart)
        success(`Loaded cart: ${importedCart.metadata.title}`)
      } catch (err) {
        error(err instanceof Error ? err.message : 'Failed to import cart')
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [loadCart, success, error],
  )

  const handleExportCart = useCallback(async () => {
    try {
      const confirmed = await exportCartAsFile(cart)
      if (confirmed) success('Cart exported successfully')
    } catch (err) {
      error('Failed to export cart')
    }
  }, [cart, success, error, info])

  const handleRunCart = useCallback(() => {
    // Reload the active console (works on both Code and Play tabs)
    reload()
  }, [reload])

  const handlePauseResume = useCallback(() => {
    pauseResume()
  }, [pauseResume])

  const handleShowShortcuts = useCallback(() => {
    setIsShortcutsModalOpen(true)
  }, [])

  // Update browser tab title with cart name
  useEffect(() => {
    document.title = cart.metadata.title
      ? `${cart.metadata.title} - FC0 Editor`
      : 'FC0 Editor'
  }, [cart.metadata.title])

  // Handle navigation state from welcome page (e.g. "View Examples" → library)
  useEffect(() => {
    const state = location.state as { view?: string } | null
    if (state?.view === 'library') {
      setActiveTab('library')
      // Clear the state so refreshing doesn't re-trigger
      window.history.replaceState({}, '')
    }
  }, [location.state, setActiveTab])

  useKeyboardShortcuts({
    onSwitchTab: setActiveTab,
    onSave: handleSave,
    onNewCart: handleNewCart,
    onImportCart: handleImportCart,
    onExportCart: handleExportCart,
    onRunCart: handleRunCart,
    onPauseResume: handlePauseResume,
    onShowShortcuts: handleShowShortcuts,
  })

  return (
    <>
      <Layout onShowShortcuts={handleShowShortcuts} />
      <NewCartModal
        isOpen={isNewCartModalOpen}
        onClose={() => setIsNewCartModalOpen(false)}
      />
      <ShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={() => setIsShortcutsModalOpen(false)}
      />
      <input
        ref={fileInputRef}
        type='file'
        accept='.json'
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </>
  )
}
